"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const PUNCT_RE = /[\p{P}\p{S}]/gu;
const REGEX_CLASS_ESCAPE_RE = /[.*+?^${}()|[\]\\/-]/g;
const HYPHEN_IN_WORD_RE = /(\p{L})-(?=\p{L})/gu;
const APOSTROPHE_IN_WORD_RE = /(\p{L})['’](?=\p{L})/gu;
const MULTI_SPACE_RE = / {2,}/g;
/*
 * A dash or slash run wedged between two letters or digits joins two words when deleted:
 * "word—word" must not become "wordword", nor "2024-05-12" "20240512". It becomes a space.
 */
const JOINER_RE = /(?<=[\p{L}\p{N}])[\p{Pd}\u2212\u2044/\\]+(?=[\p{L}\p{N}])/gu;
/* A point or comma between two digits is part of the number: "3.14", "1,000". */
const NUMBER_MARK_RE = /(\d)([.,])(?=\d)/g;

const HYPHEN_PLACEHOLDER = "";
const APOSTROPHE_PLACEHOLDER = "";
const POINT_PLACEHOLDER = "\u0003";
const COMMA_PLACEHOLDER = "\u0004";

/** English strings. A translated content file overrides these through `ui`. */
export const REMOVE_PUNCTUATION_STRINGS = {
  options: "Options",
  keepTheseCharacters: "Keep these characters",
  keepPlaceholder: "e.g. #@-",
  collapseDoubleSpaces: "Collapse double spaces",
  keepHyphensInWords: "Keep hyphens in words",
  keepApostrophesInContractions: "Keep apostrophes in contractions",
  inputText: "Input text",
  inputPlaceholder:
    "Well-known fact: it's a test — with (lots) of punctuation!",
  characterWord: "one: character|other: characters",
  removePunctuation: "Remove Punctuation",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  cleanedText: "Cleaned text",
  charactersBefore: "Characters before",
  charactersAfter: "Characters after",
  charactersRemoved: "Characters removed",
} as const;

function escapeForCharClass(chars: string): string {
  return chars.replace(REGEX_CLASS_ESCAPE_RE, "\\$&");
}

export interface RemoveOptions {
  keep: string;
  collapseSpaces: boolean;
  keepHyphens: boolean;
  keepApostrophes: boolean;
}

function buildStripRegex(keep: string): RegExp {
  if (keep.length === 0) {
    return PUNCT_RE;
  }
  const kept = escapeForCharClass(keep);
  return new RegExp(`(?![${kept}])[\\p{P}\\p{S}]`, "gu");
}

export function removePunctuation(
  text: string,
  options: RemoveOptions
): string {
  let working = text;
  if (options.keepHyphens) {
    working = working.replace(HYPHEN_IN_WORD_RE, `$1${HYPHEN_PLACEHOLDER}`);
  }
  if (options.keepApostrophes) {
    working = working.replace(
      APOSTROPHE_IN_WORD_RE,
      `$1${APOSTROPHE_PLACEHOLDER}`
    );
  }
  working = working.replace(JOINER_RE, (run) =>
    [...run].some((char) => options.keep.includes(char)) ? run : " "
  );
  working = working.replace(
    NUMBER_MARK_RE,
    (_m, digit: string, mark: string) =>
      digit + (mark === "." ? POINT_PLACEHOLDER : COMMA_PLACEHOLDER)
  );
  working = working.replace(buildStripRegex(options.keep), "");
  working = working
    .replaceAll(HYPHEN_PLACEHOLDER, "-")
    .replaceAll(APOSTROPHE_PLACEHOLDER, "'")
    .replaceAll(POINT_PLACEHOLDER, ".")
    .replaceAll(COMMA_PLACEHOLDER, ",");
  if (options.collapseSpaces) {
    working = working.replace(MULTI_SPACE_RE, " ");
  }
  return working;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function RemovePunctuationWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(REMOVE_PUNCTUATION_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [keep, setKeep] = useState("");
  const [collapseSpaces, setCollapseSpaces] = useState(true);
  const [keepHyphens, setKeepHyphens] = useState(false);
  const [keepApostrophes, setKeepApostrophes] = useState(false);
  const [output, setOutput] = useState("");
  const [hasResult, setHasResult] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleRemove() {
    if (input.length === 0) {
      return;
    }
    setOutput(
      removePunctuation(input, {
        keep,
        collapseSpaces,
        keepHyphens,
        keepApostrophes,
      })
    );
    setHasResult(true);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setHasResult(false);
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="space-y-3">
          <label
            className="flex flex-wrap items-center gap-2 text-foreground text-sm"
            htmlFor="keep-chars"
          >
            {strings.keepTheseCharacters}
            <input
              className="w-40 rounded-lg border border-border bg-card px-3 py-2 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="keep-chars"
              onChange={(e) => setKeep(e.target.value)}
              placeholder={strings.keepPlaceholder}
              type="text"
              value={keep}
            />
          </label>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
              <input
                checked={collapseSpaces}
                className="accent-primary"
                onChange={(e) => setCollapseSpaces(e.target.checked)}
                type="checkbox"
              />
              {strings.collapseDoubleSpaces}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
              <input
                checked={keepHyphens}
                className="accent-primary"
                onChange={(e) => setKeepHyphens(e.target.checked)}
                type="checkbox"
              />
              {strings.keepHyphensInWords}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
              <input
                checked={keepApostrophes}
                className="accent-primary"
                onChange={(e) => setKeepApostrophes(e.target.checked)}
                type="checkbox"
              />
              {strings.keepApostrophesInContractions}
            </label>
          </div>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="punct-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="punct-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(input.length)}
          {tPlural(strings.characterWord, locale, input.length)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleRemove}
          type="button"
        >
          {strings.removePunctuation}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {hasResult && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="punct-output"
          >
            {strings.cleanedText}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="punct-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {hasResult && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.charactersBefore}
            value={fmt(input.length)}
          />
          <StatCard
            label={strings.charactersAfter}
            value={fmt(output.length)}
          />
          <StatCard
            label={strings.charactersRemoved}
            value={fmt(Math.max(input.length - output.length, 0))}
          />
        </div>
      )}
    </>
  );
}
